import { useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Chrome,
  Mail,
  Monitor,
  Apple,
  Download,
  Copy,
  Check,
  ExternalLink,
  FileText,
  ArrowRight,
  Settings,
  Puzzle
} from 'lucide-react';

const ExtensionPage = () => {
  const { user } = useAuth();
  const [copiedStep, setCopiedStep] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const copyToClipboard = (text, step) => {
    navigator.clipboard.writeText(text);
    setCopiedStep(step);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedStep(null), 2000);
  };

  const chromeSteps = [
    {
      title: 'Download the Extension',
      desc: 'Click the button below to download the ConnectMail Chrome extension zip file.',
      action: (
        <a href={`${backendUrl}/api/extension/download`} download>
          <Button className="mt-2" data-testid="download-extension-btn">
            <Download className="w-4 h-4 mr-2" />
            Download Extension (.zip)
          </Button>
        </a>
      )
    },
    {
      title: 'Extract the ZIP File',
      desc: 'Unzip the downloaded file to a folder on your computer (e.g., Desktop/connectmail-extension).'
    },
    {
      title: 'Open Chrome Extensions',
      desc: 'Navigate to chrome://extensions in your browser. Enable "Developer mode" in the top-right corner.',
      copyText: 'chrome://extensions'
    },
    {
      title: 'Load the Extension',
      desc: 'Click "Load unpacked" and select the extracted folder. The ConnectMail icon will appear in your toolbar.'
    },
    {
      title: 'Sign In & Use',
      desc: 'Click the ConnectMail icon, enter your login credentials. Your signature will auto-inject when composing emails in Gmail.'
    }
  ];

  const gmailSteps = [
    'Go to Gmail and click the gear icon (Settings)',
    'Click "See all settings"',
    'Scroll down to the "Signature" section',
    'Click "Create new" and name it "ConnectMail"',
    'Go to your ConnectMail Signatures page and click "Copy HTML"',
    'Paste the HTML into the Gmail signature editor',
    'Click "Save Changes" at the bottom of the page'
  ];

  const outlookSteps = [
    'Open Outlook and go to File > Options > Mail > Signatures',
    'Click "New" and name the signature "ConnectMail"',
    'Go to your ConnectMail Signatures page and click "Copy HTML"',
    'Paste into the Outlook signature editor',
    'Set it as default for new messages and replies',
    'Click OK to save'
  ];

  const appleMailSteps = [
    'Open Apple Mail and go to Mail > Settings (or Preferences)',
    'Click the "Signatures" tab',
    'Click the "+" button to create a new signature',
    'Name it "ConnectMail"',
    'Go to your ConnectMail Signatures page and click "Copy HTML"',
    'Paste the content into the signature editor',
    'Drag the signature to the desired email account on the left',
    'Close the settings window'
  ];

  return (
    <Layout>
      <div className="space-y-8" data-testid="extension-page">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Install Your Signature</h1>
          <p className="text-muted-foreground mt-1">Set up ConnectMail on your favorite email platform</p>
        </div>

        <Tabs defaultValue="chrome" className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="chrome" data-testid="chrome-tab">
              <Chrome className="w-4 h-4 mr-2" />
              Chrome
            </TabsTrigger>
            <TabsTrigger value="gmail" data-testid="gmail-tab">
              <Mail className="w-4 h-4 mr-2" />
              Gmail
            </TabsTrigger>
            <TabsTrigger value="outlook" data-testid="outlook-tab">
              <Monitor className="w-4 h-4 mr-2" />
              Outlook
            </TabsTrigger>
            <TabsTrigger value="apple" data-testid="apple-tab">
              <Apple className="w-4 h-4 mr-2" />
              Apple Mail
            </TabsTrigger>
          </TabsList>

          {/* Chrome Extension */}
          <TabsContent value="chrome">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                    <Puzzle className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <CardTitle>Chrome Extension</CardTitle>
                    <CardDescription>Auto-inject your signature in Gmail compose</CardDescription>
                  </div>
                </div>
                <Badge variant="outline" className="w-fit mt-2">Recommended for Gmail users</Badge>
              </CardHeader>
              <CardContent className="space-y-4">
                {chromeSteps.map((step, i) => (
                  <div key={i} className="flex gap-4 p-4 bg-muted/30 rounded-lg">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-sm shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{step.title}</p>
                      <p className="text-sm text-muted-foreground mt-1">{step.desc}</p>
                      {step.copyText && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={() => copyToClipboard(step.copyText, `chrome-${i}`)}
                        >
                          {copiedStep === `chrome-${i}` ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                          {step.copyText}
                        </Button>
                      )}
                      {step.action}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Gmail */}
          <TabsContent value="gmail">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-red-500" />
                  Gmail Manual Setup
                </CardTitle>
                <CardDescription>Paste your signature directly in Gmail settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {gmailSteps.map((step, i) => (
                  <div key={i} className="flex gap-3 p-3 bg-muted/30 rounded-lg">
                    <span className="text-primary font-bold">{i + 1}.</span>
                    <p className="text-sm">{step}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Outlook */}
          <TabsContent value="outlook">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-blue-600" />
                  Microsoft Outlook
                </CardTitle>
                <CardDescription>Works with Outlook desktop and web</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {outlookSteps.map((step, i) => (
                  <div key={i} className="flex gap-3 p-3 bg-muted/30 rounded-lg">
                    <span className="text-primary font-bold">{i + 1}.</span>
                    <p className="text-sm">{step}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Apple Mail */}
          <TabsContent value="apple">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Apple className="w-5 h-5" />
                  Apple Mail
                </CardTitle>
                <CardDescription>macOS and iOS Mail app</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {appleMailSteps.map((step, i) => (
                  <div key={i} className="flex gap-3 p-3 bg-muted/30 rounded-lg">
                    <span className="text-primary font-bold">{i + 1}.</span>
                    <p className="text-sm">{step}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default ExtensionPage;
